<template>
    <div>
        <DataTable ref="horsePendingTable" :value="pendingHorses" lazy paginator dataKey="id"
                   v-model:rows="searchCriteria.page.limit" v-model:first="searchCriteria.page.offset" removableSort
                   :totalRecords="totalRecords" :loading="loading" @page="onPage"
                   @sort="onPage" :rowsPerPageOptions="[5,10,20,50]"
                   filterDisplay="row" v-model:filters="searchCriteria" scrollable
                   scrollHeight="calc(100vh - 300px)">
            <Column field="ueln" :header="$t('message.horsePending.table.ueln')" :sortable="true" filterField="ueln"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.ueln }}
                </template>
                <template #filter>
                    <CustomInputText name="ueln" :maxLength="15" v-model="searchCriteria.ueln.value"
                                     @update:modelValue="search" />
                </template>
            </Column>
            <Column field="name" :header="$t('message.horsePending.table.name')" :sortable="true" filterField="name"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.name }}
                </template>
                <template #filter>
                    <CustomInputText name="name" v-model="searchCriteria.name.value" @update:modelValue="search" />
                </template>
            </Column>
            <Column field="horseSexName" :header="$t('message.horsePending.table.gender')" :sortable="true" filterField="sex" sortField="sex"
                    :showFilterMenu="false" :showClearButton="false" class="w-1">
                <template #body="{ data }">
                    {{ data.horseSexName }}
                </template>
                <template #filter>
                    <CustomMultiSelectHorseSex name="sex" v-model="searchCriteria.sex.value" @update:modelValue="search" />
                </template>
            </Column>
            <Column field="coatColor" :header="$t('message.horsePending.table.coatColor')" :sortable="true" sortField="color.shortName" filterField="coatColor"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.coatColor }}
                </template>
                <template #filter>
                    <CustomMultiSelectHorseColor name="coatColorIds" v-model="searchCriteria.coatColorIds.value" item-label="name"
                                                 @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="dateOfBirth" :header="$t('message.horsePending.table.dateOfBirth')"
                    :sortable="true" filterField="dateOfBirth"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.dateOfBirth }}
                </template>
                <template #filter>
                    <Calendar name="dateOfBirth" v-model="searchCriteria.dateOfBirth.value" selectionMode="range"
                              :showTime="false" placeholder="DD-MM-YYYY" dateFormat="dd-mm-yy" showButtonBar
                              @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="previousStudbookRegistryId" :header="$t('message.horsePending.table.originalStudbookRef')" :sortable="true" filterField="previousStudbookRegistryId"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.previousStudbookRegistryId }}
                </template>
                <template #filter>
                    <CustomInputText name="previousStudbookRegistryId" v-model="searchCriteria.previousStudbookRegistryId.value"
                                     @update:modelValue="search" />
                </template>
            </Column>
            <Column field="detailedStatus" :header="$t('message.horsePending.table.detailedStatus')"
                    sortable :showFilterMenu="false" :showClearButton="false" filterField="horseStatus"
                    class="w-1">
                <template #body="{ data }">
                    {{ data.horseStatusLabel + this.getExtraStatusLabel(data.extraStatus) }}
                </template>
                <template #filter>
                    <CustomSelectOneHorseStatus name="detailedStatus" v-model="searchCriteria.horseStatus.value"
                                                @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="origin" :header="$t('message.horsePending.table.origin')"
                    sortable :showFilterMenu="false" :showClearButton="false" filterField="origin"
                    class="w-1">
                <template #body="{ data }">
                    {{ data.origin }}
                </template>
                <template #filter>
                    <CustomSelectOneHorseOrigin name="origin" v-model="searchCriteria.origin.value"
                                                @update:modelValue="search" />
                </template>
            </Column>
            <Column :header="$t('message.horsePending.table.details')" class="w-1">
                <template #body="{ data }">
                    <router-link :to="getEditHorseUrl(data)">
                        <i class="pi pi-arrow-right" />
                    </router-link>
                </template>
            </Column>
            <template #empty>
                {{ $t('message.other.emptyTable') }}
            </template>
        </DataTable>
    </div>
</template>

<script>
    import DataTable from "primevue/datatable";
    import Column from "primevue/column";
    import {FilterMatchMode} from "primevue/api";
    import Calendar from "primevue/calendar";
    import {
        searchPendingHorsesPerUserRole,
        searchPendingHorsesPerUserRoleCount,
    } from "@/swagger/vue-api-client";
    import {SearchCriteria} from "@/utils/SearchCriteria";
    import CustomInputText from "@/components/form/CustomInputText.vue";
    import CustomMultiSelectHorseColor from "@/components/form/CustomMultiSelectHorseColor.vue";
    import CustomMultiSelectHorseSex from "@/components/form/CustomMultiSelectHorseSex.vue";
    import CustomSelectOneHorseStatus from "@/components/form/CustomSelectOneHorseStatus.vue";
    import {HorseUtils} from "@/utils/HorseUtils";
    import CustomSelectOneHorseOrigin from "@/components/form/CustomSelectOneHorseOrigin.vue";

    export default {
        name: "HorsePendingTable",

        components: {
            CustomSelectOneHorseOrigin,
            DataTable,
            Column,
            CustomMultiSelectHorseSex,
            CustomMultiSelectHorseColor,
            CustomInputText,
            CustomSelectOneHorseStatus,
            Calendar,
        },

        props: {
            modelValue: null,
        },

        computed: {
        },

        data() {
            return {
                loading: false,
                totalRecords: 0,
                pendingHorses: [],
                searchCriteria: this.getClearSearchCriteria(),
            };
        },

        mounted() {
            this.search();
        },

        methods: {
            search() {
                this.onPage(this.getFirstPage());
            },

            getExtraStatusLabel(status) {
                if (!status) {
                    return "";
                }
                return " (" + status + ")";
            },

            onPage(event) {
                this.loading = true;

                this.searchCriteria.page.offset = event.first;
                this.searchCriteria.page.limit = event.rows;
                this.searchCriteria.page.sortField = event.sortField ? event.sortField : "id";
                this.searchCriteria.page.sortOrder = event.sortOrder ? event.sortOrder : -1;

                const body = SearchCriteria.prepareCriteria(this.searchCriteria);

                searchPendingHorsesPerUserRole({body}).then((response) => {
                    this.pendingHorses = response.data;
                    return searchPendingHorsesPerUserRoleCount({body})
                        .then((response) => {
                            this.totalRecords = response.data;
                            this.loading = false;
                        });
                });
            },

            getFirstPage() {
                return {
                    first: this.searchCriteria.page.offset,
                    rows: this.searchCriteria.page.limit,
                };
            },

            getEditHorseUrl(horse) {
                return HorseUtils.getHorseEditUrl(horse.horseStatus, horse.id);
            },

            getClearSearchCriteria() {
                return {
                    ueln: { value: null, matchMode: FilterMatchMode.CONTAINS },
                    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
                    sex: { value: null, matchMode: FilterMatchMode.IN },
                    coatColorIds: { value: null, matchMode: FilterMatchMode.IN },
                    dateOfBirth: { value: null, matchMode: FilterMatchMode.IN },
                    years: { value: null, matchMode: FilterMatchMode.IN },
                    previousStudbookRegistryId: { value: null, matchMode: FilterMatchMode.CONTAINS },
                    horseStatus: { value: null, matchMode: FilterMatchMode.CONTAINS},
                    origin: { value: null, matchMode: FilterMatchMode.CONTAINS},
                    page: {
                        limit: 10,
                        offset: 0,
                        sortField: null,
                        sortOrder: null,
                    },
                };
            },
        },

        watch: {
            searchCriteria: {
                handler(value) {
                    this.$emit("update:modelValue", value);
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss">
</style>
